import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from 'react';
import axios from 'axios';
import { useApp } from './AppContext';
import { useAuth } from './AuthContext';const SettingsContext = createContext(null);
import { API_URL as __API_BASE__ } from '@/lib/apiBase';
const API_URL = __API_BASE__;


export const useSettings = () => {
  const ctx = useContext(SettingsContext);
  if (!ctx) throw new Error('useSettings must be used inside <SettingsProvider>');
  return ctx;
};

/**
 * Convenience: returns the currency symbol or fallback "$".
 */
export const useCurrency = () => {
  const { settings } = useSettings();
  return settings?.general?.currency_symbol || '$';
};

/**
 * Permission check for the current user. Examples:
 *   const canRefund = useCan('orders.refund');
 *   if (!useCan('users.manage')) return <NotAuthorized />;
 */
export const useCan = (permKey) => {
  const { permissions } = useSettings();
  const { user } = useAuth();
  if (!user || !permKey) return false;
  const list = permissions?.[user.role] || [];
  return list.includes(permKey);
};

export const SettingsProvider = ({ children }) => {
  const { currentOutlet } = useApp();
  const { user } = useAuth();
  const [settings, setSettings] = useState(null);
  const [permissions, setPermissions] = useState({});

  const refresh = useCallback(async () => {
    if (!currentOutlet || !user) return;
    try {
      const [s, p] = await Promise.all([
        axios.get(`${API_URL}/api/app-settings?outlet_id=${currentOutlet.id}`, { withCredentials: true }),
        axios.get(`${API_URL}/api/permissions?outlet_id=${currentOutlet.id}`, { withCredentials: true }),
      ]);
      setSettings(s.data);
      setPermissions(p.data.permissions || {});
    } catch (e) {
      // Silent — non-critical
    }
  }, [currentOutlet, user]);

  useEffect(() => { refresh(); }, [refresh]);

  // Allow children (e.g., AppSettingsPanel) to refresh after saving.
  const value = useMemo(() => ({ settings, permissions, refresh }), [settings, permissions, refresh]);

  return <SettingsContext.Provider value={value}>{children}</SettingsContext.Provider>;
};

export default SettingsProvider;
